import React, { useContext, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FlaskConical } from "lucide-react";
import Button from "common/Buttons/Button";
import Banner from "common/Banner/Banner";
import { getIsDarkMode } from "utils/misc";
import Text from "common/Typography/Text";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useWorkspace from "utils/hooks/useWorkspace";
import { useBottomNavigator, useIsOnboarding } from "utils/hooks/misc";
import { AnimatePresence, motion } from "motion/react";
import type { HTMLMotionProps } from "motion/react";
import DemoTag from "common/Tags/DemoTag";
import { useUser } from "@shared/utils/hooks/misc";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface DemoModeBannerProps {
  onSwitchToUserWorkspace?: (switchWorkspace: () => void) => void;
}

const DemoModeBanner = ({ onSwitchToUserWorkspace }: DemoModeBannerProps) => {
  const isDarkMode = getIsDarkMode();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isShortTag = useMedia([`(max-width: 385px)`], [true], false);
  const isOnboarding = useIsOnboarding();
  const { workspace, onWorkspaceChange } = useWorkspace();
  const [showBanner, setShowBanner] = useState(false);
  const { user } = useUser();
  const { isDrawer } = useContext(DrawerContext) || {};
  const showAnimation = !user?.isFree() && !isDrawer;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();

  useEffect(() => {
    setTimeout(() => setShowBanner(workspace === WorkspaceType.DEMO), 200);
  }, [workspace]);

  if ((!showAnimation && workspace !== WorkspaceType.DEMO) || isOnboarding)
    return null;

  const switchToUserWorkspace = () => {
    onWorkspaceChange(WorkspaceType.USER);
  };

  const handleSwitchToUserWorkspaceClick = () => {
    if (onSwitchToUserWorkspace) {
      onSwitchToUserWorkspace(switchToUserWorkspace);
    } else {
      switchToUserWorkspace();
    }
  };

  const props: Pick<
    HTMLMotionProps<"div">,
    "initial" | "animate" | "exit"
  > = showAnimation
    ? {
        initial: { opacity: 0 },
        animate: {
          opacity: 1,
          transition: { ease: "easeIn", duration: 0.4 },
        },
        exit: {
          opacity: 0,
          transition: { ease: "easeOut", duration: 0.3 },
        },
      }
    : {};

  return (
    <AnimatePresence>
      {(showBanner || !showAnimation) && (
        <motion.div {...props}>
          {isNewLook ? (
            <NewLookWrapper $isBottomNavigator={isBottomNavigator}>
              <NewLookMark>
                <FlaskConical size={16} strokeWidth={1.75} />
              </NewLookMark>
              <NewLookText>
                <NewLookTitle>{t("demo_mode.banner_title")}</NewLookTitle>
                {!isMobile && ` ${t("demo_mode.banner_description")}`}
              </NewLookText>
              <NewLookButton onClick={handleSwitchToUserWorkspaceClick}>
                {t("demo_mode.banner_cta")}
              </NewLookButton>
            </NewLookWrapper>
          ) : (
            <BannerWrapper
              $isDarkMode={isDarkMode}
              $isBottomNavigator={isBottomNavigator}
              innerWrapperStyle={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DemoTag isSmall={isMobile} isShort={isShortTag} />
              <BannerText $isMobile={isMobile}>
                {!isBottomNavigator && `${t("demo_mode.active_prefix")} `}
                {t("demo_mode.banner_prefix")}
                <Link
                  style={{ marginLeft: 6 }}
                  onClick={handleSwitchToUserWorkspaceClick}
                >
                  {t("demo_mode.banner_click_here")}
                </Link>
              </BannerText>
            </BannerWrapper>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(DemoModeBanner);

const BannerWrapper = styled(Banner)<{
  $isDarkMode?: boolean;
  $isBottomNavigator?: boolean;
}>`
  background: ${(p) =>
    p.$isDarkMode
      ? "linear-gradient(90deg, #17191D 0%, #344165 100%)"
      : "linear-gradient(to right, #FFFFFF, #EFF4FF 75%, #3E6CFF51)"};
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  overflow: hidden;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px 24px;
    height: auto;
  }
  ${(p) =>
    p.$isBottomNavigator &&
    `
      padding: 6px 8px !important;
    `};
`;
const BannerText = styled(Text)<{ $isMobile?: boolean }>`
  margin-left: ${(p) => (p.$isMobile ? 12 : 8)}px;
  text-align: start;
`;
// New look: a slim, solid strip in the demo colour (no gradient, no icon
// tile), so demo mode reads as a state rather than a promotion.
const NewLookWrapper = styled.div<{ $isBottomNavigator?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px ${(p) => (p.$isBottomNavigator ? 16 : 24)}px;
  /* A quiet strip: the page colour, a hairline, and purple only on the
     mark, so demo mode is noticeable without tinting the whole width. */
  /* Light mode: a soft brand blue, as dark mode's slightly lifted strip. */
  background: ${(p) =>
    p.theme.isDarkMode ? p.theme.colors.cardBgColor : p.theme.colors.lightBlue};
  border-bottom: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  user-select: none;
`;
const NewLookMark = styled.span`
  flex: none;
  display: inline-flex;
  color: ${(p) => p.theme.colors.purple};
`;
const NewLookText = styled(Text)`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const NewLookTitle = styled.span`
  color: ${(p) => p.theme.colors.primaryText};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
// A quiet secondary action (the banner isn't the page's primary action).
const NewLookButton = styled(Button)`
  &&& {
    flex: none;
    height: 28px;
    padding: 0 12px;
    font-size: 12.5px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.primaryBlue};
    background: transparent;
    border: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
    box-shadow: none;
  }
  &&&:hover {
    background: ${(p) => p.theme.colors.lightBlue};
    border-color: ${(p) => p.theme.colors.lightBlue};
  }
`;
